import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const LoaderContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${props => props.theme.colors.cream} 0%, ${props => props.theme.colors.white} 100%);
  z-index: 1000;
  overflow: hidden;
`;

const LoaderContent = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const ProgressCircle = styled(motion.div)`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.woodLight};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 90%;
    height: 90%;
    border-radius: 50%;
    border: 2px solid ${props => props.theme.colors.wood};
    opacity: 0.3;
  }
`;

const ProgressText = styled(motion.span)`
  position: absolute;
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.wood};
  font-size: 1.5rem;
`;

const LoadingText = styled(motion.div)`
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.wood};
  font-size: clamp(1rem, 3vw, 1.5rem);
  text-align: center;
  opacity: 0.9;
`;

const NotesContainer = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Note = styled(motion.div)`
  position: absolute;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.wood};
  opacity: 0.6;
`;

const loadingTexts = [
  "Accordage des instruments...",
  "Préparation des harmonies...",
  "Création de l'ambiance...",
  "Mise en place des mélodies..."
];

const noteIcons = ["♪", "♫", "♩", "♬"];

const Loader = ({ isLoading }) => {
  const [progress, setProgress] = useState(0);
  const [currentText, setCurrentText] = useState(0);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 1;
        });
      }, 30);

      return () => clearInterval(interval);
    } else {
      setProgress(0); // Réinitialise le progrès quand le loader est masqué
    }
  }, [isLoading]);

  useEffect(() => {
    const textInterval = setInterval(() => {
      setCurrentText(prev => (prev + 1) % loadingTexts.length);
    }, 2000);

    return () => clearInterval(textInterval);
  }, []);

  useEffect(() => {
    const createNote = () => {
      const note = {
        id: Date.now(),
        icon: noteIcons[Math.floor(Math.random() * noteIcons.length)],
        x: Math.random() * 100,
        y: Math.random() * 100,
        rotation: Math.random() * 360,
      };

      setNotes(prev => [...prev, note]);
      setTimeout(() => {
        setNotes(prev => prev.filter(n => n.id !== note.id));
      }, 3000);
    };

    const noteInterval = setInterval(createNote, 500);
    return () => clearInterval(noteInterval);
  }, []);

  return (
    <AnimatePresence>
      {isLoading && (
        <LoaderContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <NotesContainer>
            {notes.map(note => (
              <Note
                key={note.id}
                initial={{ 
                  x: `${note.x}%`,
                  y: "100%",
                  rotate: note.rotation,
                  opacity: 0 
                }}
                animate={{ 
                  y: "0%",
                  opacity: [0, 1, 0],
                  rotate: [note.rotation, note.rotation + 360]
                }}
                transition={{ 
                  duration: 3,
                  ease: "easeOut"
                }}
              >
                {note.icon}
              </Note>
            ))}
          </NotesContainer>

          <LoaderContent>
            <ProgressCircle>
              <motion.div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  border: `2px solid ${props => props.theme.colors.wood}`,
                  clipPath: `polygon(50% 50%, -50% -50%, ${progress * 3.6}deg, 100% 100%)`
                }}
              />
              <ProgressText>{progress}%</ProgressText>
            </ProgressCircle>

            <LoadingText
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {loadingTexts[currentText]}
            </LoadingText>
          </LoaderContent>
        </LoaderContainer>
      )}
    </AnimatePresence>
  );
};

export default Loader;
